/**
 * Queries against one depth frame already unprojected to world space.
 *
 * Face normals were rejected for the ground slope: simulated against our own
 * measured noise (~9% of cells >6 mm proud) a single triangle's normal tilts a
 * median 34° on a genuinely flat table, re-randomised every rebuild. A
 * least-squares plane over a 10 cm disc brings that to 0.6°, which is below a
 * real 2° table slope instead of eight times above it.
 *
 * three.js `Raycaster` was rejected too: 0.27 ms per ray at grid 1/2 on
 * desktop, because a query point inside the bounding sphere with a null
 * `boundingBox` scans all ~7000 triangles with no early-out. A linear scan of
 * the world positions costs ~0.05 ms for the same answer.
 *
 * Every scan below walks `cells` and indexes `positions` at `cell * 3`, so the
 * reads are in range by construction and are asserted rather than guarded.
 */

/** One depth frame's world positions, indexed by grid cell. */
export interface SurfaceProbeInput {
  /** World XYZ per cell, three floats each. */
  positions: Float32Array
  /**
   * Depth in metres per cell, 0 for unknown. Cells reading 0 keep *stale* world
   * coordinates from the previous frame in `positions`, so every scan here must
   * skip them explicitly or the fit drags toward last frame's geometry.
   */
  valid: Float32Array
  cells: number
  /**
   * Analytic floor for this frame, if one was found. Used only where the local
   * cell scan finds too few samples to fit anything — a body that has slid past
   * the edge of the depth frame, or onto a patch the floor rejected. Freezing
   * such a body was the honest reading of "the demo forgot", but it also
   * freezes every measurement downstream of it, and this plane is still a
   * property of the current frame rather than remembered geometry.
   */
  ground: GroundAnchor | null
}

/** A plane in world space, height expressed about an explicit XZ origin. */
export interface GroundAnchor {
  readonly height: number
  readonly slopeX: number
  readonly slopeZ: number
  readonly originX: number
  readonly originZ: number
}

/** Immutable at its boundary: every producer builds a fresh one. */
export interface SupportPlane {
  /** World Y of the fitted plane directly under the query point. */
  readonly height: number
  /** dY/dx, dY/dz — dimensionless, small-angle. */
  readonly slopeX: number
  readonly slopeZ: number
  readonly samples: number
  /** RMS residual in metres. Says whether `slope` means anything. */
  readonly rms: number
}

/** The plane evaluated at one XZ point, in the form the body solver consumes. */
export function planeAt(ground: GroundAnchor, x: number, z: number): SupportPlane {
  return {
    height:
      ground.height +
      ground.slopeX * (x - ground.originX) +
      ground.slopeZ * (z - ground.originZ),
    slopeX: ground.slopeX,
    slopeZ: ground.slopeZ,
    samples: 0,
    rms: 0,
  }
}

const MIN_SAMPLES = 8

/**
 * Running sums for a least-squares plane, with `dx`/`dz` already taken relative
 * to the query point. Separate from the scan so a caller with its own inlier
 * test can reuse the solve.
 */
export interface PlaneSums {
  n: number
  sumX: number
  sumZ: number
  sumY: number
  sumXX: number
  sumXZ: number
  sumZZ: number
  sumXY: number
  sumZY: number
  sumYY: number
}

export function emptySums(): PlaneSums {
  return {
    n: 0,
    sumX: 0,
    sumZ: 0,
    sumY: 0,
    sumXX: 0,
    sumXZ: 0,
    sumZZ: 0,
    sumXY: 0,
    sumZY: 0,
    sumYY: 0,
  }
}

export function addSample(sums: PlaneSums, dx: number, dz: number, y: number): void {
  sums.n += 1
  sums.sumX += dx
  sums.sumZ += dz
  sums.sumY += y
  sums.sumXX += dx * dx
  sums.sumXZ += dx * dz
  sums.sumZZ += dz * dz
  sums.sumXY += dx * y
  sums.sumZY += dz * y
  sums.sumYY += y * y
}

export function solvePlane(sums: PlaneSums): SupportPlane | null {
  const { n, sumX, sumZ, sumY, sumXX, sumXZ, sumZZ, sumXY, sumZY, sumYY } = sums
  if (n < MIN_SAMPLES) return null

  // Symmetric normal equations for y = a + b·dx + c·dz, solved through the
  // adjugate (also symmetric, so only six cofactors are needed).
  const a00 = sumXX * sumZZ - sumXZ * sumXZ
  const a01 = sumXZ * sumZ - sumX * sumZZ
  const a02 = sumX * sumXZ - sumXX * sumZ
  const a11 = n * sumZZ - sumZ * sumZ
  const a12 = sumX * sumZ - n * sumXZ
  const a22 = n * sumXX - sumX * sumX

  const det = n * a00 + sumX * a01 + sumZ * a02

  // A patch whose cells are collinear in XZ — one grid row across a thin strip
  // of valid depth — has no defined slope. Its height still means something.
  //
  // The scale reference is the mean second moment rather than `sumXX * sumZZ`,
  // which is the obvious choice and is wrong: it collapses to zero for exactly
  // the collinear patch this guard exists to catch, so `det` was compared
  // against zero, the branch never fired, and the solve below divided by a zero
  // determinant and returned NaN heights. The comparison is inclusive so a
  // patch with every sample at the query point is caught too.
  const spread = (sumXX + sumZZ) / 2
  if (Math.abs(det) <= n * spread * spread * 1e-6) {
    const mean = sumY / n
    const variance = Math.max(0, sumYY / n - mean * mean)
    return { height: mean, slopeX: 0, slopeZ: 0, samples: n, rms: Math.sqrt(variance) }
  }

  const inv = 1 / det
  const height = (a00 * sumY + a01 * sumXY + a02 * sumZY) * inv
  const slopeX = (a01 * sumY + a11 * sumXY + a12 * sumZY) * inv
  const slopeZ = (a02 * sumY + a12 * sumXY + a22 * sumZY) * inv

  // Residual sum in closed form. Valid only because the coefficients satisfy
  // the normal equations, and it saves a second pass over the disc.
  const sse = Math.max(0, sumYY - height * sumY - slopeX * sumXY - slopeZ * sumZY)

  return { height, slopeX, slopeZ, samples: n, rms: Math.sqrt(sse / n) }
}

/**
 * Same fit with the normal held at world +Y, so only the height is free.
 *
 * Slope is the noisiest thing a plane fit produces — it is estimated from the
 * residuals, which on ARCore are spatially correlated blobs rather than
 * independent speckle, so a tilt is exactly the shape the noise takes. Where
 * the surface is known to be gravity-aligned (a floor, a table) dropping the
 * two slope terms removes that degree of freedom instead of measuring it, and
 * the reference space is already gravity-aligned so +Y is the right axis.
 *
 * Measured worse than the free-slope fit on device — 51% of cells locked
 * against 77% — because over a 1.5 m radius a 12 mm band is only a 0.46° tilt
 * budget. Kept because that is how the default was chosen; not reachable from
 * the demo's controls.
 */
export function solveLevel(sums: PlaneSums): SupportPlane | null {
  const { n, sumY, sumYY } = sums
  if (n < MIN_SAMPLES) return null

  const height = sumY / n
  const variance = Math.max(0, sumYY / n - height * height)
  return { height, slopeX: 0, slopeZ: 0, samples: n, rms: Math.sqrt(variance) }
}

/**
 * Cells nearer than this fraction of the ring radius are skipped: their bearing
 * from the query point is dominated by cell quantisation, not by where the
 * obstacle actually is.
 */
const RING_INNER_FRACTION = 0.35

/** Running sums over cells within `radius` in XZ, relative to the query point. */
export function scanSums(
  positions: Float32Array,
  valid: Float32Array,
  cells: number,
  x: number,
  z: number,
  radius: number
): PlaneSums {
  const radiusSq = radius * radius
  const sums = emptySums()

  for (let cell = 0; cell < cells; cell += 1) {
    if (valid[cell] === 0) continue
    const offset = cell * 3
    const dx = positions[offset]! - x
    const dz = positions[offset + 2]! - z
    if (dx * dx + dz * dz > radiusSq) continue
    addSample(sums, dx, dz, positions[offset + 1]!)
  }

  return sums
}

/** Least-squares plane over cells within `radius` in XZ. Null below MIN_SAMPLES. */
export function fitSupport(
  positions: Float32Array,
  valid: Float32Array,
  cells: number,
  x: number,
  z: number,
  radius: number
): SupportPlane | null {
  return solvePlane(scanSums(positions, valid, cells, x, z, radius))
}

/**
 * Max height above `plane` per direction, on a ring of `out.length` bins at
 * `radius`. NaN where a bin has no cell. Returns the count of valid bins.
 */
export function probeRing(
  positions: Float32Array,
  valid: Float32Array,
  cells: number,
  x: number,
  z: number,
  radius: number,
  plane: SupportPlane,
  out: Float32Array
): number {
  out.fill(NaN)

  const bins = out.length
  const inner = radius * RING_INNER_FRACTION
  const innerSq = inner * inner
  const outerSq = radius * radius
  const perBin = bins / (Math.PI * 2)

  for (let cell = 0; cell < cells; cell += 1) {
    if (valid[cell] === 0) continue
    const offset = cell * 3
    const dx = positions[offset]! - x
    const dz = positions[offset + 2]! - z
    const distanceSq = dx * dx + dz * dz
    if (distanceSq < innerSq || distanceSq > outerSq) continue

    const height =
      positions[offset + 1]! - (plane.height + plane.slopeX * dx + plane.slopeZ * dz)

    let angle = Math.atan2(dz, dx)
    if (angle < 0) angle += Math.PI * 2
    const bin = Math.min(bins - 1, Math.floor(angle * perBin))

    if (Number.isNaN(out[bin]!) || height > out[bin]!) out[bin] = height
  }

  let filled = 0
  for (let bin = 0; bin < bins; bin += 1) if (!Number.isNaN(out[bin]!)) filled += 1
  return filled
}

const directionCache = new Map<number, Float32Array>()

/** Unit XZ direction of each ring bin, interleaved. Cached per bin count. */
export function binDirections(bins: number): Float32Array {
  const cached = directionCache.get(bins)
  if (cached) return cached

  const table = new Float32Array(bins * 2)
  for (let bin = 0; bin < bins; bin += 1) {
    const angle = ((bin + 0.5) / bins) * Math.PI * 2
    table[bin * 2] = Math.cos(angle)
    table[bin * 2 + 1] = Math.sin(angle)
  }
  directionCache.set(bins, table)
  return table
}
